import type { LucideIcon } from 'lucide-react';

import { Card, CardContent } from './card';
import { cn } from '../../lib/utils';

export type KpiAccent = 'emerald' | 'sky' | 'violet' | 'amber' | 'rose' | 'indigo';

/** Cuadrados de color legacy (consumidos por la app flexibilitypay). El Merchant BO ya no los usa. */
const ACCENT_STYLES: Record<KpiAccent, string> = {
  emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  sky: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-500',
  rose: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  indigo: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
};

const TREND_STYLES = {
  up: 'text-brand-teal',
  down: 'text-brand-red',
  neutral: 'text-muted-foreground',
} as const;

export interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
  icon: LucideIcon;
  /** Legacy: cuadrado de color detrás del ícono. Si se omite, se usa ícono outline navy (marca). */
  accent?: KpiAccent;
  /** KPI protagonista: fondo navy, mayor jerarquía visual. */
  primary?: boolean;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    label: string;
  };
  className?: string;
}

export function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  accent,
  primary = false,
  trend,
  className,
}: KpiCardProps) {
  return (
    <Card
      className={cn(
        'gap-2 py-6',
        primary && 'border-primary bg-primary text-primary-foreground',
        className,
      )}
    >
      <CardContent className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <span
            className={cn(
              'text-[10px] font-semibold uppercase tracking-[0.1em]',
              primary ? 'text-brand-teal dark:text-brand-navy/70' : 'text-muted-foreground',
            )}
          >
            {label}
          </span>
          <span
            className={cn('truncate font-semibold tracking-tight', primary ? 'text-3xl' : 'text-2xl')}
          >
            {value}
          </span>
          {(hint || trend) && (
            <div className="flex items-center gap-2 text-xs">
              {trend && (
                <span className={cn('font-medium', TREND_STYLES[trend.direction])}>
                  {trend.direction === 'up' && '▲ '}
                  {trend.direction === 'down' && '▼ '}
                  {trend.label}
                </span>
              )}
              {hint && (
                <span className={primary ? 'text-primary-foreground/60' : 'text-muted-foreground'}>
                  {hint}
                </span>
              )}
            </div>
          )}
        </div>
        {accent && !primary ? (
          <div
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-xl',
              ACCENT_STYLES[accent],
            )}
          >
            <Icon className="size-5" />
          </div>
        ) : (
          <Icon
            className={cn(
              'size-5 shrink-0',
              primary ? 'text-brand-teal dark:text-brand-navy' : 'text-brand-navy dark:text-brand-teal',
            )}
            strokeWidth={1.75}
          />
        )}
      </CardContent>
    </Card>
  );
}
